import { Routes } from "@angular/router";
import { EnergyDataService } from "src/services/energy-data.service";
import { NavigationDataService } from "src/services/navigationmenu-data.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { allwaysCanActivate, auth0CanActivate, auth0CanMatch } from "../util/auth0/auth0.guard";
import { BasePageComponent } from "./base-page/base-page.component";
import { EvMapComponent } from "./ev-map/ev-map.component";
import { EV_MAP_ROUTES } from "./ev-map/ev-map.routes";
import { FourOhFourComponent } from "./four-oh-four/four-oh-four.component";
import { INFOPAGE_ROUTES } from "./infopage/infopage.routes";
import { PublicRouterOutletComponent } from "./public-router-outlet.component";
import { UnsubscribeComponent } from "./public/unsubscribe/unsubscribe.component";
import { RedirectPageComponent } from "./redirect-page/redirect-page.component";
import { EvMapResolver } from "./resolvers/evmap-resolver";
import { InfopageResolver } from "./resolvers/infopage-resolver";
import { OwnerSearchResolver } from "./resolvers/ownersearch-resolver";
import { SearchpageResolver } from "./resolvers/searchpage-resolver";
import { ValueEstateResolver } from "./resolvers/value-estate-resolver";
import { FourOhThreeComponent } from "./four-oh-three/four-oh-three.component";
import { ExternalReportResolver } from "./resolvers/externalreport-resolver";
import { CompanyCheckSearchResolver } from "./resolvers/companychecksearch-resolver";
import { CustomerCheckSearchResolver } from "./resolvers/customerchecksearch-resolver";
import { EstimateResolver } from "./resolvers/estimate-resolver";
import { MunicipalityMapResolver } from "./resolvers/municipality-map.resolver";
import { TopographyMapResolver } from "./resolvers/topography-map.resolver";
import { ReportsLoadingPageComponent } from "./reports-loading-page/reports-loading-page.component";
import { AppraisalResolver } from "./resolvers/appraisal-resolver";
import { CreateAppraisalResolver } from "./resolvers/create-appraisal-resolver";
import { CommercialAppraisalResolver } from "./resolvers/commercialappraisaloverview-resolver";

export const routes: Routes = [...publicRoutes(), ...nonPublicRoutes()];

/**
 * Add routes here that is public (non guarded).
 */
export function publicRoutes(): Routes {
    return [
        {
            path: "public",
            component: PublicRouterOutletComponent,
            children: [
                {
                    path: "unsubscribe",
                    component: UnsubscribeComponent,
                    canActivate: [allwaysCanActivate],
                },
                // Other public routes...
            ],
        },
        // Other public routes...
        // If added here remember to add the path name to app.component.ts
    ];
}

/**
 *  Add routes here that is guarded by authentication.
 */
export function nonPublicRoutes(): Routes {
    return [
        {
            path: "",
            component: BasePageComponent,
            providers: [NavigationDataService],
            canActivate: [auth0CanActivate],
        },
        {
            path: "frontpage",
            loadComponent: () => import("./frontpage/frontpage.component").then((c) => c.FrontpageComponent),
            providers: [NavigationDataService],
            canMatch: [auth0CanMatch],
            canActivate: [auth0CanActivate],
            data: { routePermissions: [EVServiceType.Internal_Debug] },
        },
        {
            path: "image",
            loadComponent: () => import("./shared-components/ev-image-viewer/ev-image-viewer.component").then((c) => c.EvImageViewerComponent),
            providers: [NavigationDataService],
            canMatch: [auth0CanMatch],
            canActivate: [auth0CanActivate],
            data: { routePermissions: [EVServiceType.Internal_Debug] },
        },
        {
            path: "energy/:unitId",
            providers: [EnergyDataService, NavigationDataService],
            loadComponent: () => import("./energy-estimate/energy-estimate.component").then((c) => c.EnergyEstimateComponent),
            loadChildren: () => import("./energy-estimate/energy-estimate.routes").then((m) => m.ENERGY_ESTIMATE_ROUTES),
            canMatch: [auth0CanMatch],
            canActivate: [auth0CanActivate],
            data: { routePermissions: [EVServiceType.EvWeb_EnergyCalculation] },
        },
        {
            path: "new-dwelling-pulse",
            providers: [NavigationDataService],
            loadComponent: () => import("./new-dwelling-pulse/new-dwelling-pulse.component").then((c) => c.NewDwellingPulseComponent),
            loadChildren: () => import("./new-dwelling-pulse/new-dwelling-pulse.routes").then((m) => m.NEW_DWELLING_PULSE_ROUTES),
            canMatch: [auth0CanMatch],
            canActivate: [auth0CanActivate],
            data: { routePermissions: [EVServiceType.Internal_Debug] },
        },
        {
            path: "infopage",
            providers: [NavigationDataService],
            canActivate: [auth0CanActivate],
            children: INFOPAGE_ROUTES,
        },
        {
            path: "searchpage",
            loadComponent: () => import("./searchpage/searchpage.component").then((c) => c.SearchpageComponent),
            providers: [NavigationDataService],
            canMatch: [auth0CanMatch],
            canActivate: [auth0CanActivate],
            data: { routePermissions: [EVServiceType.Internal_Debug] },
        },
        {
            path: "map",
            providers: [NavigationDataService],
            component: EvMapComponent,
            children: EV_MAP_ROUTES,
            canActivate: [auth0CanActivate],
        },
        {
            path: "areabuilder",
            providers: [NavigationDataService],
            loadComponent: () => import("./area-builder/area-builder.component").then((c) => c.AreaBuilderComponent),
            loadChildren: () => import("./area-builder/area-builder.routes").then((m) => m.AREA_BUILDER_ROUTES),
            canActivate: [auth0CanActivate],
        },
        {
            path: "resolve",
            component: BasePageComponent,
            children: [
                {
                    path: "searchpage",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: SearchpageResolver },
                },
                {
                    path: "infopage",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: InfopageResolver },
                },
                {
                    path: "valuateEstate",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: ValueEstateResolver },
                },
                {
                    path: "ownersearch",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: OwnerSearchResolver },
                },
                {
                    path: "customerchecksearch",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: CustomerCheckSearchResolver },
                },
                {
                    path: "companychecksearch",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: CompanyCheckSearchResolver },
                },
                {
                    path: "map",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: EvMapResolver },
                },
                {
                    path: "externalreport",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: ExternalReportResolver },
                },
                {
                    path: "estimate",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: EstimateResolver },
                },
                {
                    path: "appraisal",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: AppraisalResolver },
                },
                {
                    path: "municipalitymap",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: MunicipalityMapResolver },
                },
                {
                    path: "topographymap",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: TopographyMapResolver },
                },
                {
                    path: "create-appraisal",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: CreateAppraisalResolver },
                },
                {
                    path: "commercial-appraisal-overview",
                    providers: [NavigationDataService],
                    component: RedirectPageComponent,
                    resolve: { data: CommercialAppraisalResolver },
                },
            ],
        },
        {
            path: "pagenotfound",
            providers: [NavigationDataService],
            component: FourOhFourComponent,
            canActivate: [auth0CanActivate],
        },
        {
            path: "reports/loading",
            providers: [NavigationDataService],
            component: ReportsLoadingPageComponent,
        },
        {
            path: "403",
            providers: [NavigationDataService],
            component: FourOhThreeComponent,
            canActivate: [auth0CanActivate],
        },
        {
            path: "**",
            redirectTo: "pagenotfound",
        },
    ];
}
